import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../baseQuery";
import type {
  ApiResponse,
  Province,
  District,
  Neighborhood,
  PaginatedResponse,
} from "../types";

type LocationRow = Record<string, unknown>;

function extractLocationRows(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }

  if (typeof raw === "string" && raw.trim()) {
    try {
      return extractLocationRows(JSON.parse(raw) as unknown);
    } catch {
      return [];
    }
  }

  if (!raw || typeof raw !== "object") {
    return [];
  }

  const record = raw as Record<string, unknown>;

  if (Array.isArray(record.data)) {
    return record.data;
  }
  return [];
}

function readLocationText(row: LocationRow, keys: readonly string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value === null || value === undefined) {
      continue;
    }
    const text = String(value).trim();
    if (text) {
      return text;
    }
  }
  return "";
}

function normalizeProvince(row: unknown): Province {
  const item = (row ?? {}) as LocationRow;
  const name = readLocationText(item, ["name", "title", "state", "city"]);
  const slug = readLocationText(item, [
    "slug",
    "state",
    "city",
    "name",
    "title",
  ]);
  const id = readLocationText(item, ["id", "slug", "state"]);
  const districts = extractLocationRows(item.districts).map(normalizeDistrict);

  return {
    id,
    name: name || slug,
    slug: slug || name,

    districts,

    districts_count:
      typeof item.districts_count === "number"
        ? item.districts_count
        : undefined,
    dt_create: typeof item.dt_create === "string" ? item.dt_create : undefined,
    dt_update: typeof item.dt_update === "number" ? item.dt_update : undefined,
  };
}

function normalizeDistrict(row: unknown): District {
  const item = (row ?? {}) as LocationRow;
  const name = String(item.name ?? "");
  const slug = readLocationText(item, [
    "slug",
    "district",
    "city",
    "name",
    "title",
  ]);
  const id = readLocationText(item, ["id", "slug", "district"]);

  return {
    id,
    province_id: readLocationText(item, [
      "province_id",
      "state_id",
      "parent_id",
    ]),
    name: name || slug,
    slug: slug || name,
    neighborhoods: extractLocationRows(item.neighborhoods).map(
      normalizeNeighborhood,
    ),
    neighborhoods_count:
      typeof item.neighborhoods_count === "number"
        ? item.neighborhoods_count
        : undefined,
    dt_create: typeof item.dt_create === "string" ? item.dt_create : undefined,
    dt_update: typeof item.dt_update === "number" ? item.dt_update : undefined,
  };
}

function normalizeNeighborhood(row: unknown): Neighborhood {
  const item = (row ?? {}) as LocationRow;
  const name = readLocationText(item, [
    "name",
    "title",
    "neighborhood",
    "district",
  ]);
  const slug = readLocationText(item, [
    "slug",
    "neighborhood",
    "name",
    "title",
  ]);
  const id = readLocationText(item, ["id", "slug", "neighborhood"]);

  return {
    id,
    district_id: readLocationText(item, ["district_id", "parent_id"]),
    name: name || slug,
    slug: slug || name,
    dt_create: typeof item.dt_create === "string" ? item.dt_create : undefined,
    dt_update: typeof item.dt_update === "number" ? item.dt_update : undefined,
  };
}

function normalizeLocationListResponse<T>(
  raw: unknown,
  mapRow?: (row: unknown) => T,
): PaginatedResponse<T> {
  const response = raw as Partial<PaginatedResponse<T>> & {
    data?: unknown;
    total?: number | string;
    skip?: number | string;
    take?: number | string;
    size?: number | string;
  };
  const extractedRows = extractLocationRows(response?.data ?? raw);
  const rows = mapRow
    ? extractedRows
        .map(mapRow)
        .filter((row) => Boolean((row as { id?: string }).id))
    : (extractedRows as T[]);
  const totalValue = Number(response?.total ?? response?.size);
  const skipValue = Number(response?.skip ?? 0);
  const takeValue = Number(response?.take ?? rows.length);

  return {
    data: rows,
    total:
      Number.isFinite(totalValue) && totalValue >= 0 ? totalValue : rows.length,
    skip: Number.isFinite(skipValue) && skipValue >= 0 ? skipValue : 0,
    take:
      Number.isFinite(takeValue) && takeValue >= 0 ? takeValue : rows.length,
  };
}

export interface ProvinceFilters {
  search?: string;
  skip?: number;
  take?: number;
  sort_by?: string;
  asc?: boolean;
}

export interface DistrictFilters {
  province_id: string;
  search?: string;
  skip?: number;
  take?: number;
  sort_by?: string;
  asc?: boolean;
}

export interface NeighborhoodFilters {
  district_id: string;
  search?: string;
  skip?: number;
  take?: number;
  sort_by?: string;
  asc?: boolean;
}

export interface ProvincePayload {
  id?: string;
  name: string;
  slug: string;
}

export interface DistrictPayload {
  id?: string;
  province_id: string;
  name: string;
  slug: string;
}

export interface NeighborhoodPayload {
  id?: string;
  district_id: string;
  name: string;
}

export const locationApi = createApi({
  reducerPath: "locationApi",
  baseQuery: customBaseQuery,
  tagTypes: ["Province", "District", "Neighborhood"],

  endpoints: (builder) => ({
    getProvinces: builder.query<
      PaginatedResponse<Province>,
      ProvinceFilters | void
    >({
      query: (params) => ({
        url: "/provinces",
        method: "GET",
        params: {
          skip: 0,
          take: 200,
          asc: true,
          sort_by: "name",
          ...(params ?? {}),
          search: params?.search?.trim() || undefined,
        },
      }),
      transformResponse: (raw: unknown): PaginatedResponse<Province> =>
        normalizeLocationListResponse(raw, normalizeProvince),
      providesTags: ["Province"],
    }),

    getProvince: builder.query<Province, string>({
      query: (id) => ({
        url: `/province/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Province", id }],
    }),

    createProvince: builder.mutation<Province, ProvincePayload>({
      query: (data) => ({
        url: "/province",
        method: "POST",
        body: {
          id: "",
          name: data.name,
          slug: data.slug,
        },
      }),
      invalidatesTags: ["Province"],
    }),

    updateProvince: builder.mutation<
      Province,
      ProvincePayload & { id: string }
    >({
      query: (data) => ({
        url: "/province",
        method: "POST",
        body: {
          id: data.id,
          name: data.name,
          slug: data.slug,
        },
      }),
      invalidatesTags: ["Province"],
    }),

    deleteProvince: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/province/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Province"],
    }),

    createDistrict: builder.mutation<District, DistrictPayload>({
      query: (data) => ({
        url: "/district",
        method: "POST",
        body: {
          id: "",
          province_id: data.province_id,
          parent_id: data.province_id,
          name: data.name,
          slug: data.slug,
        },
      }),
      invalidatesTags: ["District", "Province"],
    }),

    updateDistrict: builder.mutation<
      District,
      DistrictPayload & { id: string }
    >({
      query: (data) => ({
        url: "/district",
        method: "POST",
        body: {
          id: data.id,
          province_id: data.province_id,
          parent_id: data.province_id,
          name: data.name,
          slug: data.slug,
        },
      }),
      invalidatesTags: ["District", "Province"],
    }),

    deleteDistrict: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/district/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["District", "Province"],
    }),

    createNeighborhood: builder.mutation<Neighborhood, NeighborhoodPayload>({
      query: (data) => ({
        url: "/neighborhood",
        method: "POST",
        body: {
          id: "",
          district_id: data.district_id,
          parent_id: data.district_id,
          name: data.name,
        },
      }),
      invalidatesTags: ["Neighborhood", "District"],
    }),

    updateNeighborhood: builder.mutation<
      Neighborhood,
      NeighborhoodPayload & { id: string }
    >({
      query: (data) => ({
        url: "/neighborhood",
        method: "POST",
        body: {
          id: data.id,
          district_id: data.district_id,
          parent_id: data.district_id,
          name: data.name,
        },
      }),
      invalidatesTags: ["Neighborhood", "District"],
    }),

    deleteNeighborhood: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/neighborhood/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Neighborhood", "District"],
    }),
  }),
});

export const {
  useGetProvincesQuery,
  useGetProvinceQuery,
  useCreateProvinceMutation,
  useUpdateProvinceMutation,
  useDeleteProvinceMutation,

  useCreateDistrictMutation,
  useUpdateDistrictMutation,
  useDeleteDistrictMutation,

  useCreateNeighborhoodMutation,
  useUpdateNeighborhoodMutation,
  useDeleteNeighborhoodMutation,
} = locationApi;
